import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
  type Query,
} from 'firebase/firestore'
import type { Auth } from 'firebase/auth'
import type { Comment, CommentableType } from '@domain/comment'
import type { CommentRepository } from '@domain/ports/finance-repository'
import {
  optionalText,
  requireIso,
  requireOneOf,
  requireText,
  textOr,
  toIso,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * A thread de comentários de uma entidade.
 *
 * ## A consulta filtra por `entityId` SÓ, e o tipo é conferido em memória
 *
 * O caminho óbvio seria `where('entityType','==') + where('entityId','==') +
 * orderBy('createdAt')`. Duas igualdades mais uma ordenação em terceiro campo
 * **exigem índice composto**, e índice que falta não falha no emulador: falha em
 * produção, com `FAILED_PRECONDITION`, na primeira vez que alguém abre a tela.
 *
 * Com uma igualdade só, o índice de campo único que o Firestore mantém sozinho
 * basta — e o conjunto que volta é minúsculo, porque `entityId` é o id de **uma**
 * entidade. O `entityType` é conferido depois: ele existe para o caso implausível
 * de um produto e uma transação compartilharem id, e conferi-lo em memória custa
 * nada sobre uma lista de poucos itens.
 *
 * A ordenação também é em memória, pelo mesmo motivo do `invoice-repository.ts`.
 *
 * ## `authorType` e `authorId` não vêm da tela
 *
 * `'user'` e o uid de quem está logado são a única combinação que
 * `firestore.rules` aceita no `create` — e é este adapter, que conhece a sessão,
 * quem os preenche. Deixar a tela escolher permitiria a ela afirmar que a IA
 * disse o que uma pessoa disse.
 *
 * **Sem sessão, a escrita nem sai.** O `throw` daqui é melhor que o
 * `permission-denied` que a regra devolveria: ele nomeia a causa.
 */

const AUTHOR_TYPES = ['user', 'system', 'ai'] as const
const ENTITY_TYPES = [
  'transaction',
  'product',
  'receipt',
  'list',
  'listItem',
  'category',
  'invoice',
] as const

/**
 * **As duas listas acima são de runtime**, e o `tsc` não as liga sozinhas aos
 * tipos. Mesma armadilha de `CASH_IMPACTS`: um valor que exista no tipo e falte
 * aqui faz **todo comentário daquele tipo lançar na leitura** — e `'ai'` e
 * `'system'` são escritos por pipelines que ainda não existem, então o erro só
 * apareceria meses depois.
 */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
const _authorsCobremAUniao: Equals<Comment['authorType'], (typeof AUTHOR_TYPES)[number]> = true
const _entidadesCobremAUniao: Equals<CommentableType, (typeof ENTITY_TYPES)[number]> = true
void _authorsCobremAUniao
void _entidadesCobremAUniao

function commentsPath(): string {
  return `households/${HOUSEHOLD_ID}/comments`
}

/**
 * Firestore → domínio.
 *
 * **`content` cai num default em vez de lançar**, ao contrário de valor e data
 * numa transação: um comentário sem texto é feio na tela, não errado, e derrubar
 * a thread inteira por causa de um documento estranho seria pior do que mostrar
 * uma linha vazia.
 */
export function toComment(id: string, data: DocumentData): Comment {
  const where = `comments/${id}`
  return {
    id,
    householdId: HOUSEHOLD_ID,
    entityType: requireOneOf(data['entityType'], ENTITY_TYPES, `${where}.entityType`),
    entityId: requireText(data['entityId'], `${where}.entityId`),
    authorType: requireOneOf(data['authorType'], AUTHOR_TYPES, `${where}.authorType`),
    authorId: optionalText(data['authorId']),
    content: textOr(data['content'], ''),
    // **`toIso`, não `optionalText`.** O adapter grava `serverTimestamp()`, que
    // volta como `Timestamp` — e `optionalText` devolve `null` para tudo que
    // não é string. Com ela, um comentário editado leria `editedAt: null` e a
    // tela nunca diria "editado", em silêncio, para sempre.
    editedAt: toIso(data['editedAt']),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${where}.updatedAt`),
  }
}

function byEntityQuery(db: Firestore, entityId: string): Query {
  return query(collection(db, commentsPath()), where('entityId', '==', entityId))
}

/** O registro que `firestore-indexes.test.ts` varre. Ver `INVOICE_QUERIES`. */
export const COMMENT_QUERIES: Record<string, (db: Firestore, id: string) => Query> = {
  listFor: byEntityQuery,
}

export function createFirebaseCommentRepository(db: Firestore, auth: Auth): CommentRepository {
  const uid = (): string => {
    const current = auth.currentUser?.uid
    if (current === undefined) {
      throw new Error('comment-repository: não há sessão — nenhum comentário pode ser atribuído.')
    }
    return current
  }

  return {
    listFor: async (entityType, entityId) => {
      const snap = await getDocs(byEntityQuery(db, entityId))
      return snap.docs
        .map((c) => toComment(c.id, c.data()))
        .filter((c) => c.entityType === entityType)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    },

    create: async (entityType, entityId, content) => {
      const created = await addDoc(collection(db, commentsPath()), {
        entityType,
        entityId,
        // A única combinação que a regra aceita. Ver o cabeçalho.
        authorType: 'user',
        authorId: uid(),
        content,
        editedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return created.id
    },

    edit: async (id, content) => {
      await updateDoc(doc(db, commentsPath(), id), {
        content,
        // `editedAt` é o que a tela usa para dizer "editado" — e é ele, não
        // `updatedAt`, porque `updatedAt` muda por qualquer escrita e diria
        // "editado" sobre um comentário que ninguém reescreveu.
        editedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    },

    remove: async (id) => {
      await deleteDoc(doc(db, commentsPath(), id))
    },
  }
}
